import type { MetaDescriptor } from 'react-router';
import type { CanonicalHreflangData, RouteMetaArgs } from '~/types/meta-args';
import type { APCountry } from '~/modules/root';
import type { SupportedCountry } from '@archipro-website/localisation';
import {
    DEFAULT_MARKET,
    getCountryHost,
    getCountryHrefLang,
    SupportedCountries,
} from '@archipro-website/localisation';
import invariant from 'tiny-invariant';
import { getMetaBaseUrl } from '../util/create-meta-base-url';
import { articleAvailableCountries } from '../util/article-indexability';
import { getRouteData } from '~/modules/root/util/get-route-data';

export interface AvailabilityZone {
    Country: {
        Code: APCountry;
    };
}

interface HreflangOption {
    code: SupportedCountry;
    lang: string;
    host: string;
}

/**
 * hreflang alternates for every supported market, derived from the shared
 * country config. Adding a market to `SupportedCountries` automatically wires
 * its canonical host and hreflang here — no per-country edits required.
 */
const HREFLANG_OPTIONS: HreflangOption[] = SupportedCountries.map((code) => ({
    code,
    lang: getCountryHrefLang(code),
    host: getCountryHost(code, { https: true }),
}));

interface GetCanonicalHreflangOptions {
    forceAllRegions?: boolean;
    /**
     * Markets to advertise, instead of every supported market or the page's
     * availability zones. Used when some markets share the path and others do
     * not yet (US products redirect to a coming-soon page until launch).
     */
    regions?: readonly SupportedCountry[];
    professionalCountry?: APCountry | string | null;
    noAlternateLinks?: boolean;
    /**
     * Category pages: a page with no availability zones is shown in every
     * market (AvailabilityZoneDomainFilterPolicy), so advertise every market.
     * Location pages are skipped because locations belong to one market.
     */
    allMarketsWhenUnzoned?: boolean;
}

/**
 * hreflang for a product directory page. NZ and AU catalogues share a path.
 * The US host answers `/products/*` with a 302 to the coming-soon page until
 * `us_product_launch` is on, and an alternate must be a 200 of the same page.
 */
export const productDirectoryHreflangOptions = (
    usProductLaunch: boolean,
): GetCanonicalHreflangOptions =>
    usProductLaunch
        ? { allMarketsWhenUnzoned: true }
        : { regions: ['NZ', 'AU'], allMarketsWhenUnzoned: true };

/** Country host config for a market, or undefined for an unknown/unsupported code. */
const hreflangOptionFor = (
    countryCode: APCountry | string | null | undefined,
): HreflangOption | undefined =>
    HREFLANG_OPTIONS.find((option) => option.code === countryCode);

/** @deprecated Use CanonicalHreflangData — kept for mergeMeta generic compatibility. */
export type CanonicalHreflangLoaderArgs = () => CanonicalHreflangData;

/** Meta input accepted from route MetaFunction and RouteMetaArgs SEO helpers. */
export type CanonicalHreflangMetaInput = {
    data?: CanonicalHreflangData | undefined;
    loaderData?: CanonicalHreflangData | undefined;
    location: RouteMetaArgs['location'];
    matches: RouteMetaArgs['matches'];
};

/** Markets to advertise as hreflang alternates for a page. */
const resolveAlternateMarkets = (
    availableCountries: ReadonlyArray<string>,
    pathname: string,
    {
        forceAllRegions,
        regions,
        noAlternateLinks,
        allMarketsWhenUnzoned,
    }: GetCanonicalHreflangOptions,
): HreflangOption[] => {
    if (noAlternateLinks) return [];
    if (forceAllRegions) return HREFLANG_OPTIONS;
    if (
        allMarketsWhenUnzoned &&
        availableCountries.length === 0 &&
        isLocationPath(pathname)
    )
        return [];

    // Named regions restrict the page's zones; an unzoned page takes the
    // named regions themselves, so an unlaunched market remains excluded.
    const namedRegions = regions?.length
        ? regions.filter(
              (code) =>
                  availableCountries.length === 0 ||
                  availableCountries.includes(code),
          )
        : undefined;
    if (
        !namedRegions &&
        allMarketsWhenUnzoned &&
        availableCountries.length === 0
    ) {
        return HREFLANG_OPTIONS;
    }
    const countries = namedRegions ?? availableCountries;
    if (countries.length <= 1) return [];
    return HREFLANG_OPTIONS.filter(({ code }) => countries.includes(code));
};

/** Normalises react-router MetaArgs for canonical/hreflang helpers (v7 loader-type variance). */
export const toCanonicalHreflangMetaInput = (props: {
    location: RouteMetaArgs['location'];
    matches: readonly unknown[];
    data?: unknown;
    loaderData?: unknown;
}): CanonicalHreflangMetaInput => ({
    location: props.location,
    matches: props.matches as RouteMetaArgs['matches'],
    data: (props.data ?? props.loaderData) as CanonicalHreflangData | undefined,
    loaderData: (props.loaderData ?? props.data) as
        | CanonicalHreflangData
        | undefined,
});

export const getCanonicalHreflang = (
    props: Parameters<typeof toCanonicalHreflangMetaInput>[0],
    options: GetCanonicalHreflangOptions = { forceAllRegions: false },
): MetaDescriptor[] => {
    const normalized = toCanonicalHreflangMetaInput(props);
    const { location, matches, data } = normalized;
    const { professionalCountry } = options;

    const { rootData } = getRouteData(matches);
    invariant(
        rootData?.localisation.country,
        'rootData is required for product category SEO',
    );
    const countryCode: APCountry = rootData.localisation.country;

    // SEO URLs ignore filters; compute the cleaned path once and reuse it for
    // the canonical, alternates and pagination links.
    const cleanPath = removeFilterPathParameters(location.pathname);

    // Handle pagination
    const queryParams = new URLSearchParams(location.search);
    const pageQueryParam = queryParams.get('page');
    const selfUrl = getMetaBaseUrl(normalized) + cleanPath;
    // Dont append page number query param if first page
    const prevPageUrl =
        data?.pagination?.prevPage === 1
            ? selfUrl
            : selfUrl + `?page=${data?.pagination?.prevPage}`;
    const nextPageUrl = selfUrl + `?page=${data?.pagination?.nextPage}`;
    // Only a real later page (2, 3, …) is one; a page of 0, 1 or not a whole
    // number is the listing's first page for the canonical and the alternates.
    const pageNumber = Number(pageQueryParam);
    const pagination =
        Number.isInteger(pageNumber) && pageNumber > 1
            ? `?page=${pageNumber}`
            : '';

    const canonicalOverride = data?.localisation?.Canonical;
    // Resolve the canonical host from the shared country config. Fall back to
    // the request origin so an unknown/unsupported country can never produce
    // an `undefined` host (which previously broke every US canonical).
    const canonicalHost =
        hreflangOptionFor(professionalCountry || countryCode)?.host ??
        getMetaBaseUrl(normalized);
    const canonicalHref = canonicalHost + cleanPath + pagination;

    // De-duplicate zone country codes so a page available in several regions of
    // one market (e.g. two NZ zones) counts as a single market. Without this,
    // `resolveAlternateMarkets` would treat an effectively single-market article
    // as multi-market and wrongly switch on hreflang alternates. Shared with the
    // article route's indexability/canonical logic via `articleAvailableCountries`.
    const availableCountries = articleAvailableCountries(data?.localisation);

    // A later page of a listing gets no alternates: markets hold different
    // numbers of items, so page N on one host can be past the last page on
    // another, which redirects, and an alternate must be a 200 of the same
    // page. The first page carries the alternates for the listing.
    const alternateLinks = (
        pagination === ''
            ? resolveAlternateMarkets(
                  availableCountries,
                  location.pathname,
                  options,
              )
            : []
    ).map((country) => {
        return {
            tagName: 'link',
            rel: 'alternate',
            href: removeTrailingSlash(country.host + cleanPath),
            hrefLang: country.lang,
        };
    });

    // When we expose alternates, advertise an x-default for users whose
    // locale matches no specific market. It points at the origin market host.
    const xDefaultLinks =
        alternateLinks.length > 0
            ? [
                  {
                      tagName: 'link',
                      rel: 'alternate',
                      href: removeTrailingSlash(
                          getCountryHost(DEFAULT_MARKET, { https: true }) +
                              cleanPath,
                      ),
                      hrefLang: 'x-default',
                  },
              ]
            : [];

    return [
        {
            tagName: 'link',
            rel: 'canonical',
            href: removeTrailingSlash(canonicalOverride ?? canonicalHref),
        },
        ...alternateLinks,
        ...xDefaultLinks,
        ...(data?.pagination && !data?.pagination?.isFirstPage
            ? [
                  {
                      tagName: 'link',
                      rel: 'prev',
                      href: removeTrailingSlash(prevPageUrl),
                  },
              ]
            : []),
        ...(data?.pagination && !data?.pagination?.isLastPage
            ? [
                  {
                      tagName: 'link',
                      rel: 'next',
                      href: removeTrailingSlash(nextPageUrl),
                  },
              ]
            : []),
    ];
};

export const removeTrailingSlash = (str: string): string =>
    str.replace(/\/+$/, '');

/** Location pages: `location-{slug}` segments or a `location_` filter. */
const isLocationPath = (pathname: string): boolean =>
    pathname
        .split('/')
        .some(
            (segment) =>
                segment.startsWith('location-') ||
                segment.startsWith('location_'),
        );

/**
 * SEO Urls should not be impacted by filters.
 * e.g https://archipro.co.nz/products/company_abi-interiors?professionals_size=60
 * should be canonicalised to
 * https://archipro.co.nz/products
 *
 * Remove trailing slash from URL for SEO purposes (avoid wasting crawl budget).
 * URLs with trailing slash are treated as a unique URL, which will flag as a duplicate page
 * when compared to the original page with no trailing slash
 *
 * We can remove all path params that have an underscore (_) these are reserved for filters
 */
const removeFilterPathParameters = (pathname: string): string => {
    const filteredPath = pathname
        .split('/')
        .filter((path) => !path.includes('_'))
        .join('/')
        .replace(/\/$/, '');
    return filteredPath;
};
